import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { apiFetch, getToken } from '../../api/client';
import API_CONFIG from '../../config/api';
import { exportMapaPropostasExcel, exportMapaPropostasPdf } from '../../pages/contratos/utils/mapaPropostasExport';

const CRITERIOS = [
  { key: 'valor', label: 'Valor da proposta (R$)' },
  { key: 'prazoExecucao', label: 'Prazo de execução' },
  { key: 'documentacao', label: 'Situação da documentação' },
  { key: 'dataEnvio', label: 'Data de envio' },
  { key: 'observacao', label: 'Observações / ressalvas' },
];

function normalizarProposta(p, idx) {
  return {
    id: p.id || `row-${idx}`,
    empresa: p.empresa || p.razao_social || p.email || `Fornecedor ${idx + 1}`,
    cnpj: p.cnpj || '',
    valor: p.valor ?? '',
    prazoExecucao: p.prazoExecucao || p.prazo_execucao || '',
    documentacao:
      p.documentacao ||
      (Array.isArray(p.documentos) && p.documentos.length
        ? 'completa'
        : p.documentacaoCompleta === true || p.documentacaoCompleta === 'completa'
          ? 'completa'
          : p.documentacaoCompleta === false || p.documentacaoCompleta === 'incompleta'
            ? 'incompleta'
            : p.status_documentacao || ''),
    dataEnvio: p.dataEnvio || p.created_at || '',
    observacao: p.observacao || p.descricao || '',
    pontuacao: p.pontuacao ?? '',
    documentos: Array.isArray(p.documentos) ? p.documentos : [],
  };
}

/**
 * Mapa comparativo de propostas do processo (não confundir com AnaliseComparativa multiunidade).
 * Persistência só via API.
 */
export default function MapaComparativoPropostasPanel({ processoId, onConcluido = null }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fornecedores, setFornecedores] = useState([]);
  const [vencedorCnpj, setVencedorCnpj] = useState('');
  const [observacaoGeral, setObservacaoGeral] = useState('');
  const [concluido, setConcluido] = useState(false);
  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');
  const [processoCodigo, setProcessoCodigo] = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [iaResultado, setIaResultado] = useState(null);
  const [linkPrestador, setLinkPrestador] = useState('');
  const [numeroOficial, setNumeroOficial] = useState('');

  const carregar = useCallback(async () => {
    if (!processoId) return;
    setLoading(true);
    setErro('');
    try {
      const [resMapa, resExt] = await Promise.all([
        apiFetch(`/api/contratos/processos/${processoId}/analise-comparativa`),
        apiFetch(`/api/contratos/processos/${processoId}/extensoes`),
      ]);
      const data = await resMapa.json().catch(() => ({}));
      const ext = await resExt.json().catch(() => ({}));
      if (!resMapa.ok) throw new Error(data.error || 'Erro ao carregar propostas');

      setProcessoCodigo(data.processo?.codigo || '');
      setObservacaoGeral(data.observacao || '');
      setVencedorCnpj(data.vencedorCnpj || '');
      if (resExt.ok) {
        setConcluido(!!ext.mapaComparativoConcluido);
        const token = ext.editalToken || ext.editalAtual?.token || null;
        const path =
          ext.editalLinkPath ||
          ext.editalAtual?.linkPath ||
          (token ? `/portal-prestador/propostas/${token}` : '');
        setLinkPrestador(path ? `${window.location.origin}${path}` : '');
        setNumeroOficial(ext.editalNumeroOficial || ext.editalAtual?.numeroOficial || '');
      }

      const itens = Array.isArray(data.itens) ? data.itens : [];
      const propostas = Array.isArray(data.propostas) ? data.propostas : [];

      if (itens.length) {
        setFornecedores(itens.map((it, i) => normalizarProposta(it, i)));
      } else if (propostas.length) {
        setFornecedores(propostas.map((p, i) => normalizarProposta(p, i)));
      } else {
        setFornecedores([]);
      }
    } catch (e) {
      setErro(e.message || 'Falha ao carregar mapa');
    } finally {
      setLoading(false);
    }
  }, [processoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const menorValorCnpj = useMemo(() => {
    let min = null;
    let cnpj = '';
    fornecedores.forEach((f) => {
      const v = Number(f.valor);
      if (!Number.isFinite(v)) return;
      if (min == null || v < min) {
        min = v;
        cnpj = f.cnpj || '';
      }
    });
    return cnpj;
  }, [fornecedores]);

  const atualizar = (idx, field, value) => {
    setFornecedores((prev) => prev.map((f, i) => (i === idx ? { ...f, [field]: value } : f)));
  };

  const salvar = async ({ marcarConcluido = false } = {}) => {
    if (!processoId) return;
    setSaving(true);
    setErro('');
    setMsg('');
    try {
      const itens = fornecedores.map((f) => ({
        empresa: f.empresa,
        cnpj: f.cnpj,
        valor: f.valor,
        prazoExecucao: f.prazoExecucao,
        documentacao: f.documentacao,
        dataEnvio: f.dataEnvio,
        observacao: f.observacao,
        pontuacao: f.pontuacao,
      }));
      const res = await apiFetch(`/api/contratos/processos/${processoId}/analise-comparativa`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itens,
          vencedorCnpj,
          observacao: observacaoGeral,
          concluido: marcarConcluido || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar mapa');

      if (marcarConcluido) {
        const resExt = await apiFetch(`/api/contratos/processos/${processoId}/extensoes`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mapaComparativoConcluido: true }),
        });
        const extData = await resExt.json().catch(() => ({}));
        if (!resExt.ok) throw new Error(extData.error || 'Erro ao marcar mapa como concluído');
        setConcluido(true);
        onConcluido?.(true);
      }

      setMsg(marcarConcluido ? 'Mapa concluído e salvo.' : 'Mapa comparativo salvo.');
    } catch (e) {
      setErro(e.message || 'Falha ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const baixarDoc = (propostaId, docId) => {
    const token = getToken();
    const url = new URL(
      `${API_CONFIG.BACKEND_URL}/api/contratos/processos/${processoId}/propostas/${propostaId}/documentos/${docId}`
    );
    if (token) url.searchParams.set('access_token', token);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const analisarComIa = async () => {
    if (!processoId) return;
    setIaLoading(true);
    setErro('');
    setMsg('');
    try {
      const res = await apiFetch(`/api/contratos/processos/${processoId}/analise-comparativa/ia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha na análise por IA');
      setIaResultado(data.analise || null);
      if (data.analise?.vencedorCnpj) setVencedorCnpj(String(data.analise.vencedorCnpj));
      if (data.analise?.recomendacao || data.analise?.resumo) {
        setObservacaoGeral(
          [data.analise.recomendacao, data.analise.resumo].filter(Boolean).join('\n\n')
        );
      }
      setMsg('Análise por IA concluída (até 3 propostas: preço × benefício).');
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro na IA');
    } finally {
      setIaLoading(false);
    }
  };

  if (!processoId) return null;
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, borderColor: 'primary.light' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700} color="primary">
          Mapa comparativo de propostas
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {numeroOficial ? (
            <Chip size="small" color="success" label={`Edital n° ${numeroOficial}`} />
          ) : null}
          {linkPrestador ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(linkPrestador);
                  setMsg('Link do prestador copiado.');
                } catch {
                  setErro('Não foi possível copiar o link.');
                }
              }}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Copiar link do prestador
            </Button>
          ) : null}
          {concluido && <Chip icon={<CheckCircleIcon />} label="Concluído" color="success" size="small" />}
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Até 3 propostas do edital. Clique no prestador para ver/baixar os PDFs. Use a IA para avaliar
        menor preço e melhor benefício.
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}
      {msg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}

      {fornecedores.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            Propostas recebidas — documentos
          </Typography>
          {fornecedores.map((f, idx) => (
            <Accordion key={f.id || idx} disableGutters sx={{ mb: 1, borderRadius: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography fontWeight={800}>{f.empresa}</Typography>
                  <Chip size="small" label={f.cnpj || 'sem CNPJ'} variant="outlined" />
                  {f.valor !== '' && f.valor != null && (
                    <Chip
                      size="small"
                      color="primary"
                      label={`R$ ${Number(f.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                    />
                  )}
                  <Chip
                    size="small"
                    label={`${(f.documentos || []).length} PDF(s)`}
                    color={(f.documentos || []).length ? 'success' : 'default'}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {!(f.documentos || []).length ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum documento anexado.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {(f.documentos || []).map((d) => (
                      <Stack
                        key={d.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {d.tipo || 'Documento'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {d.nome}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<PictureAsPdfIcon />}
                          onClick={() => baixarDoc(f.id, d.id)}
                          sx={{ textTransform: 'none' }}
                        >
                          PDF
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {iaResultado && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            Recomendação da IA
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
            {iaResultado.recomendacao || iaResultado.resumo || '—'}
          </Typography>
          {Array.isArray(iaResultado.ranking) && iaResultado.ranking.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {iaResultado.ranking.map((r, i) => (
                <Typography key={i} variant="caption" display="block">
                  {i + 1}. {r.empresa} — geral {r.notaGeral}/10 (preço {r.notaPreco}, benefício{' '}
                  {r.notaBeneficio}) — {r.motivo}
                </Typography>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {!fornecedores.length ? (
        <Alert severity="info">Nenhuma proposta vinculada a editais deste processo ainda.</Alert>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, minWidth: 160 }}>Critério</TableCell>
                {fornecedores.map((f, idx) => {
                  const isMenor = f.cnpj && f.cnpj === menorValorCnpj;
                  const isVantajosa = f.cnpj && f.cnpj === vencedorCnpj;
                  return (
                    <TableCell
                      key={f.id || idx}
                      align="center"
                      sx={{
                        minWidth: 180,
                        fontWeight: 700,
                        bgcolor: isVantajosa
                          ? 'rgba(46,125,50,0.12)'
                          : isMenor
                            ? 'rgba(25,118,210,0.08)'
                            : undefined,
                        border: isVantajosa ? '2px solid #2e7d32' : undefined,
                      }}
                    >
                      <Typography variant="body2" fontWeight={800}>
                        {f.empresa}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {f.cnpj || '—'}
                      </Typography>
                      {isMenor && (
                        <Chip label="Menor valor" size="small" color="info" sx={{ mt: 0.5 }} />
                      )}
                      {isVantajosa && (
                        <Chip label="Mais vantajosa" size="small" color="success" sx={{ mt: 0.5, ml: 0.5 }} />
                      )}
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant={isVantajosa ? 'contained' : 'outlined'}
                          color="success"
                          onClick={() => setVencedorCnpj(f.cnpj || '')}
                          sx={{ textTransform: 'none', fontSize: 11 }}
                          disabled={!f.cnpj}
                        >
                          Marcar como mais vantajosa
                        </Button>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {CRITERIOS.map((c) => (
                <TableRow key={c.key}>
                  <TableCell sx={{ fontWeight: 700 }}>{c.label}</TableCell>
                  {fornecedores.map((f, idx) => {
                    const isVantajosa = f.cnpj && f.cnpj === vencedorCnpj;
                    if (c.key === 'documentacao') {
                      const completa =
                        String(f.documentacao || '').toLowerCase() === 'completa' ||
                        f.documentacao === true;
                      return (
                        <TableCell
                          key={`${f.id}-${c.key}`}
                          align="center"
                          sx={{ bgcolor: isVantajosa ? 'rgba(46,125,50,0.06)' : undefined }}
                        >
                          <Stack spacing={1} alignItems="center">
                            <Chip
                              label={completa ? 'Documentação completa' : 'Incompleta'}
                              color={completa ? 'success' : 'error'}
                              size="small"
                            />
                            <Button
                              size="small"
                              sx={{ textTransform: 'none' }}
                              onClick={() =>
                                atualizar(idx, 'documentacao', completa ? 'incompleta' : 'completa')
                              }
                            >
                              Alternar
                            </Button>
                          </Stack>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={`${f.id}-${c.key}`}
                        align="center"
                        sx={{ bgcolor: isVantajosa ? 'rgba(46,125,50,0.06)' : undefined }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          type={c.key === 'valor' ? 'number' : 'text'}
                          value={f[c.key] ?? ''}
                          onChange={(e) => atualizar(idx, c.key, e.target.value)}
                          multiline={c.key === 'observacao'}
                          minRows={c.key === 'observacao' ? 2 : 1}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TextField
        label="Parecer geral do mapa"
        size="small"
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        value={observacaoGeral}
        onChange={(e) => setObservacaoGeral(e.target.value)}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }} justifyContent="flex-end">
        <Button
          variant="outlined"
          color="secondary"
          startIcon={iaLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          onClick={analisarComIa}
          disabled={iaLoading || !fornecedores.length}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Analisar com IA (preço × benefício)
        </Button>
        <Button
          startIcon={<FileDownloadIcon />}
          onClick={() =>
            exportMapaPropostasExcel(fornecedores, {
              processoCodigo,
              vencedorCnpj,
            })
          }
          disabled={!fornecedores.length}
          sx={{ textTransform: 'none' }}
        >
          Excel
        </Button>
        <Button
          startIcon={<PictureAsPdfIcon />}
          onClick={() =>
            exportMapaPropostasPdf(fornecedores, {
              processoCodigo,
              vencedorCnpj,
              observacao: observacaoGeral,
            })
          }
          disabled={!fornecedores.length}
          sx={{ textTransform: 'none' }}
        >
          PDF
        </Button>
        <Button
          variant="outlined"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={() => salvar({ marcarConcluido: false })}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          Salvar
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={() => salvar({ marcarConcluido: true })}
          disabled={saving || !vencedorCnpj}
          sx={{ textTransform: 'none' }}
        >
          Concluir mapa
        </Button>
      </Stack>
    </Paper>
  );
}
